import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Users,
  Search,
  Filter,
  Plus,
  CheckCircle2,
  XCircle,
  Clock,
  Sparkles,
  Building2,
  Mail,
  Phone,
  ShieldCheck,
  ShieldAlert,
  Eye,
  EyeOff,
  Share2,
  Copy,
  Layers,
  Award,
  Radio,
  FileText,
  AlertTriangle,
  RotateCw,
  Sliders,
  Check,
  ChevronRight,
  ExternalLink,
} from 'lucide-react';
import {
  fetchAdminParticipants,
  createAdminParticipant,
  updateAdminParticipant,
  fetchApplications,
  approveApplication,
  rejectApplication,
  fetchSponsorLimits,
  createSponsorLimit,
  ParticipantRecord,
  ApplicationRecord,
  SponsorLimitRecord,
  ManualParticipantPayload,
} from '../lib/api';

interface AdminParticipantsApplicationsProps {
  adminKey: string;
}

const ROLE_OPTIONS = [
  { value: 'all', label: 'All Roles' },
  { value: 'vip', label: 'VIP Guests' },
  { value: 'speaker', label: 'Speakers' },
  { value: 'student_innovator', label: 'Student Innovators' },
  { value: 'media', label: 'Media' },
  { value: 'organiser', label: 'Organisers' },
  { value: 'sponsor', label: 'Sponsors' },
  { value: 'exhibitor', label: 'Exhibitors' },
  { value: 'delegate', label: 'Conference Delegates' },
  { value: 'visitor', label: 'Trade Visitors' },
  { value: 'contractor', label: 'Contractors' },
];

export const AdminParticipantsApplications: React.FC<AdminParticipantsApplicationsProps> = ({ adminKey }) => {
  const queryClient = useQueryClient();
  const [activeMainTab, setActiveMainTab] = useState<'roster' | 'applications' | 'sponsors'>('roster');

  // Filters for Roster
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRole, setSelectedRole] = useState('all');
  const [statusFilter, setStatusFilter] = useState<'all' | 'printed' | 'pending' | 'checked_in'>('all');

  // Filters for Applications
  const [appTypeFilter, setAppTypeFilter] = useState<'all' | 'speaker' | 'media' | 'student_innovator'>('all');
  const [appStatusFilter, setAppStatusFilter] = useState<'all' | 'pending' | 'approved' | 'rejected'>('pending');
  const [appSearch, setAppSearch] = useState('');

  // Modals state
  const [isAddParticipantOpen, setIsAddParticipantOpen] = useState(false);
  const [editingParticipant, setEditingParticipant] = useState<ParticipantRecord | null>(null);
  const [approvingApp, setApprovingApp] = useState<ApplicationRecord | null>(null);
  const [rejectingApp, setRejectingApp] = useState<ApplicationRecord | null>(null);
  const [isSponsorModalOpen, setIsSponsorModalOpen] = useState(false);

  // Form states
  const [newParticipant, setNewParticipant] = useState<ManualParticipantPayload>({
    fullName: '',
    email: '',
    phone: '',
    companyName: '',
    jobTitle: '',
    primaryRole: 'delegate',
    secondaryRoles: [],
    tier: 'conference_delegate',
    stallNumber: '',
    assignedPod: '',
    directoryOptOut: false,
    shareContactOptOut: false,
  });

  const [approvePod, setApprovePod] = useState('');
  const [rejectReason, setRejectReason] = useState('');

  const [newSponsorLimit, setNewSponsorLimit] = useState({
    sponsorTier: 'gold',
    companyName: '',
    vipPassLimit: 2,
    delegatePassLimit: 5,
    exhibitorPassLimit: 4,
    visitorPassLimit: 10,
  });

  const [copiedCode, setCopiedCode] = useState<string | null>(null);

  // Query Participants
  const participantsQuery = useQuery({
    queryKey: ['adminParticipants', selectedRole, statusFilter, searchQuery],
    queryFn: () =>
      fetchAdminParticipants(adminKey, {
        role: selectedRole,
        badgeStatus: statusFilter === 'printed' ? 'printed' : statusFilter === 'pending' ? 'pending' : undefined,
        checkedIn: statusFilter === 'checked_in' ? 'true' : undefined,
        search: searchQuery,
      }),
  });

  // Query Applications
  const applicationsQuery = useQuery({
    queryKey: ['adminApplications', appTypeFilter, appStatusFilter, appSearch],
    queryFn: () =>
      fetchApplications(adminKey, {
        type: appTypeFilter,
        status: appStatusFilter,
        search: appSearch,
      }),
  });

  // Query Sponsor Limits
  const sponsorLimitsQuery = useQuery({
    queryKey: ['sponsorLimits'],
    queryFn: () => fetchSponsorLimits(adminKey),
  });

  // Mutations
  const addParticipantMutation = useMutation({
    mutationFn: (payload: ManualParticipantPayload) => createAdminParticipant(adminKey, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminParticipants'] });
      setIsAddParticipantOpen(false);
      setNewParticipant({
        fullName: '',
        email: '',
        phone: '',
        companyName: '',
        jobTitle: '',
        primaryRole: 'delegate',
        secondaryRoles: [],
        tier: 'conference_delegate',
        stallNumber: '',
        assignedPod: '',
        directoryOptOut: false,
        shareContactOptOut: false,
      });
    },
  });

  const updateParticipantMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: any }) =>
      updateAdminParticipant(adminKey, id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminParticipants'] });
      setEditingParticipant(null);
    },
  });

  const approveAppMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: any }) =>
      approveApplication(adminKey, id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminApplications'] });
      queryClient.invalidateQueries({ queryKey: ['adminParticipants'] });
      setApprovingApp(null);
      setApprovePod('');
    },
  });

  const rejectAppMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      rejectApplication(adminKey, id, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminApplications'] });
      setRejectingApp(null);
      setRejectReason('');
    },
  });

  const createSponsorLimitMutation = useMutation({
    mutationFn: (payload: any) => createSponsorLimit(adminKey, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sponsorLimits'] });
      setIsSponsorModalOpen(false);
    },
  });

  const handleCopy = (code: string) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const participants = participantsQuery.data?.participants || [];
  const summary = participantsQuery.data?.summary;
  const applicationsList = (applicationsQuery.data?.applications || []).filter(
    (app) => app.type !== 'speaker'
  );
  const appMetrics = applicationsQuery.data?.metrics;
  const sponsorLimits = sponsorLimitsQuery.data?.limits || [];

  return (
    <div className="space-y-6">
      {/* Top Banner & Main Sub-tab Navigation */}
      <div className="bg-white border border-slate-200/90 rounded-2xl p-5 flex flex-col md:flex-row md:items-center justify-between gap-4 shadow-xs">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="p-1.5 rounded-lg bg-[#0A3D27]/10 text-[#0A3D27]">
              <Users className="w-4 h-4" />
            </span>
            <h3 className="text-base font-black text-slate-900">
              Participants Directory &amp; Applications Inbox
            </h3>
          </div>
          <p className="text-xs text-slate-500">
            Manage official participant rosters, review Student Innovator Pods &amp; Media Press accreditation, and govern sponsor pass limits.
          </p>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {activeMainTab === 'roster' && (
            <button
              onClick={() => setIsAddParticipantOpen(true)}
              className="flex items-center gap-1.5 px-3.5 py-1.5 bg-[#0A3D27] hover:bg-[#062B1B] text-white rounded-xl text-xs font-bold transition-all shadow-xs cursor-pointer"
            >
              <Plus className="w-3.5 h-3.5" />
              <span>+ Add Participant (Manual)</span>
            </button>
          )}

          {activeMainTab === 'sponsors' && (
            <button
              onClick={() => setIsSponsorModalOpen(true)}
              className="flex items-center gap-1.5 px-3.5 py-1.5 bg-[#0A3D27] hover:bg-[#062B1B] text-white rounded-xl text-xs font-bold transition-all shadow-xs cursor-pointer"
            >
              <Plus className="w-3.5 h-3.5" />
              <span>Set Sponsor Pass Limit</span>
            </button>
          )}

          <button
            onClick={() => {
              participantsQuery.refetch();
              applicationsQuery.refetch();
              sponsorLimitsQuery.refetch();
            }}
            disabled={participantsQuery.isFetching || applicationsQuery.isFetching}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-xs font-semibold transition-colors cursor-pointer border border-slate-200"
          >
            <RotateCw
              className={`w-3.5 h-3.5 text-[#0A3D27] ${
                participantsQuery.isFetching || applicationsQuery.isFetching ? 'animate-spin' : ''
              }`}
            />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* Main Tabs Navigation */}
      <div className="flex items-center gap-2 border-b border-slate-200 pb-2">
        <button
          type="button"
          onClick={() => setActiveMainTab('roster')}
          className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer ${
            activeMainTab === 'roster'
              ? 'bg-[#0A3D27] text-white shadow-xs'
              : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
          }`}
        >
          <Users className="w-3.5 h-3.5" />
          <span>Unified Participants Roster ({summary?.total || 0})</span>
        </button>

        <button
          type="button"
          onClick={() => setActiveMainTab('applications')}
          className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer ${
            activeMainTab === 'applications'
              ? 'bg-[#0A3D27] text-white shadow-xs'
              : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
          }`}
        >
          <Sparkles className="w-3.5 h-3.5" />
          <span>
            Applications Inbox {appMetrics?.pending ? `(${appMetrics.pending} Pending)` : ''}
          </span>
        </button>

        <button
          type="button"
          onClick={() => setActiveMainTab('sponsors')}
          className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer ${
            activeMainTab === 'sponsors'
              ? 'bg-[#0A3D27] text-white shadow-xs'
              : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
          }`}
        >
          <Award className="w-3.5 h-3.5" />
          <span>Sponsor Pass Limits ({sponsorLimits.length})</span>
        </button>
      </div>

      {/* ======================================================== */}
      {/* TAB 1: UNIFIED PARTICIPANTS ROSTER                      */}
      {/* ======================================================== */}
      {activeMainTab === 'roster' && (
        <div className="space-y-4">
          {/* Metrics summary cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="bg-white border border-slate-200/90 rounded-2xl p-4 shadow-2xs">
              <span className="text-[11px] font-bold text-slate-500 uppercase">Total Participants</span>
              <div className="text-xl font-black text-slate-900 mt-1">{summary?.total || 0}</div>
            </div>
            <div className="bg-white border border-slate-200/90 rounded-2xl p-4 shadow-2xs">
              <span className="text-[11px] font-bold text-slate-500 uppercase">Badges Printed</span>
              <div className="text-xl font-black text-[#0A3D27] mt-1">{summary?.printed || 0}</div>
            </div>
            <div className="bg-white border border-slate-200/90 rounded-2xl p-4 shadow-2xs">
              <span className="text-[11px] font-bold text-slate-500 uppercase">Checked-in / Admitted</span>
              <div className="text-xl font-black text-amber-700 mt-1">{summary?.checkedIn || 0}</div>
            </div>
            <div className="bg-white border border-slate-200/90 rounded-2xl p-4 shadow-2xs">
              <span className="text-[11px] font-bold text-slate-500 uppercase">VIPs &amp; Speakers</span>
              <div className="text-xl font-black text-[#C5A059] mt-1">
                {(summary?.byRole?.vip || 0) + (summary?.byRole?.speaker || 0)}
              </div>
            </div>
          </div>

          {/* Search & Filters */}
          <div className="bg-white border border-slate-200/90 rounded-2xl p-4 space-y-3 shadow-xs">
            <div className="flex flex-col md:flex-row gap-3">
              <div className="relative flex-1">
                <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  placeholder="Search by name, email, company, badge code, phone..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-9 pr-4 py-2 border border-slate-200 rounded-xl text-xs focus:ring-2 focus:ring-[#0A3D27] focus:outline-hidden"
                />
              </div>

              {/* Status pills */}
              <div className="flex items-center gap-1.5 flex-wrap">
                {(['all', 'printed', 'pending', 'checked_in'] as const).map((st) => (
                  <button
                    key={st}
                    onClick={() => setStatusFilter(st)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                      statusFilter === st
                        ? 'bg-slate-900 text-white'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                  >
                    {st === 'all'
                      ? 'All Status'
                      : st === 'printed'
                      ? 'Printed'
                      : st === 'pending'
                      ? 'Pending Print'
                      : 'Checked In'}
                  </button>
                ))}
              </div>
            </div>

            {/* Role Filter Chips */}
            <div className="flex items-center gap-1.5 overflow-x-auto pb-1 text-xs">
              <span className="text-[11px] font-bold text-slate-400 uppercase shrink-0">Role:</span>
              {ROLE_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => setSelectedRole(opt.value)}
                  className={`px-2.5 py-1 rounded-md text-[11px] font-bold whitespace-nowrap transition-all cursor-pointer ${
                    selectedRole === opt.value
                      ? 'bg-[#0A3D27] text-white shadow-2xs'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Participants Table */}
          <div className="bg-white border border-slate-200/90 rounded-2xl overflow-hidden shadow-xs">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead className="bg-[#FAF5EA] border-b border-[#E6C687]/40 text-slate-700 uppercase font-black tracking-wider text-[10px]">
                  <tr>
                    <th className="py-3 px-4">Participant</th>
                    <th className="py-3 px-4">Primary Role &amp; Tier</th>
                    <th className="py-3 px-4">Stall / Pod</th>
                    <th className="py-3 px-4">Badge Code</th>
                    <th className="py-3 px-4">Privacy &amp; Status</th>
                    <th className="py-3 px-4 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {participantsQuery.isLoading ? (
                    <tr>
                      <td colSpan={6} className="p-8 text-center text-slate-500">
                        <RotateCw className="w-5 h-5 animate-spin mx-auto text-[#0A3D27] mb-1" />
                        <span>Loading participants directory...</span>
                      </td>
                    </tr>
                  ) : participants.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="p-8 text-center text-slate-400">
                        No participants match the selected filters.
                      </td>
                    </tr>
                  ) : (
                    participants.map((p) => (
                      <tr key={p.id} className="hover:bg-slate-50/80 transition-colors">
                        <td className="py-3 px-4">
                          <div className="font-bold text-slate-900">{p.fullName}</div>
                          <div className="text-[11px] text-slate-500">{p.jobTitle || 'Participant'}</div>
                          <div className="text-[11px] font-medium text-[#0A3D27]">{p.companyName || '—'}</div>
                        </td>

                        <td className="py-3 px-4">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span
                              className={`px-2 py-0.5 rounded-md text-[10px] font-black uppercase ${
                                p.theme?.badgeClass || 'bg-slate-100 text-slate-800'
                              }`}
                            >
                              {p.theme?.label || p.primaryRole.toUpperCase()}
                            </span>
                            {p.roles?.length > 1 && (
                              <span className="text-[9px] font-bold text-slate-400">
                                +{p.roles.length - 1} role{p.roles.length - 1 > 1 ? 's' : ''}
                              </span>
                            )}
                          </div>
                          <div className="text-[10px] text-slate-400 mt-0.5">{p.email}</div>
                        </td>

                        <td className="py-3 px-4">
                          {p.assignedPod ? (
                            <span className="px-2 py-0.5 rounded-md bg-lime-50 border border-lime-300 text-lime-900 text-[10px] font-bold uppercase">
                              Innovation {p.assignedPod}
                            </span>
                          ) : p.stallNumber ? (
                            <span className="px-2 py-0.5 rounded-md bg-sky-50 border border-sky-300 text-sky-900 text-[10px] font-bold uppercase">
                              Stall {p.stallNumber}
                            </span>
                          ) : (
                            <span className="text-slate-400 text-[11px]">—</span>
                          )}
                        </td>

                        <td className="py-3 px-4">
                          {p.badgeCode ? (
                            <div className="flex items-center gap-1.5">
                              <span className="font-mono text-[11px] font-bold text-slate-800 bg-slate-100 px-1.5 py-0.5 rounded border border-slate-200">
                                {p.badgeCode}
                              </span>
                              <button
                                onClick={() => handleCopy(p.badgeCode!)}
                                className="p-1 hover:bg-slate-200 rounded text-slate-500 cursor-pointer"
                                title="Copy Badge Code"
                              >
                                {copiedCode === p.badgeCode ? (
                                  <Check className="w-3 h-3 text-emerald-600" />
                                ) : (
                                  <Copy className="w-3 h-3" />
                                )}
                              </button>
                            </div>
                          ) : (
                            <span className="text-amber-600 text-[10px] font-semibold">Unprovisioned</span>
                          )}
                          {p.nfcTagId && (
                            <div className="flex items-center gap-1 text-[9.5px] font-mono text-emerald-700 mt-0.5">
                              <Radio className="w-2.5 h-2.5" />
                              <span>NFC Linked</span>
                            </div>
                          )}
                        </td>

                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            {p.checkedIn ? (
                              <span className="px-2 py-0.5 rounded-md bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px] font-bold">
                                Admitted
                              </span>
                            ) : p.badgeStatus === 'printed' ? (
                              <span className="px-2 py-0.5 rounded-md bg-sky-50 text-sky-700 border border-sky-200 text-[10px] font-bold">
                                Printed
                              </span>
                            ) : (
                              <span className="px-2 py-0.5 rounded-md bg-slate-100 text-slate-600 border border-slate-200 text-[10px]">
                                Registered
                              </span>
                            )}

                            {p.directoryOptOut ? (
                              <span title="Directory Hidden">
                                <EyeOff className="w-3.5 h-3.5 text-slate-400" />
                              </span>
                            ) : (
                              <span title="Visible in Directory">
                                <Eye className="w-3.5 h-3.5 text-slate-400" />
                              </span>
                            )}
                          </div>
                        </td>

                        <td className="py-3 px-4 text-right">
                          <button
                            onClick={() => setEditingParticipant(p)}
                            className="px-2.5 py-1 bg-white hover:bg-slate-100 text-slate-700 border border-slate-200 rounded-lg text-xs font-semibold shadow-2xs transition-colors cursor-pointer"
                          >
                            Edit &amp; Privacy
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ======================================================== */}
      {/* TAB 2: APPLICATIONS INBOX (STUDENT PODS & MEDIA PRESS)   */}
      {/* ======================================================== */}
      {activeMainTab === 'applications' && (
        <div className="space-y-4">
          {/* Applications Header & Type Filters */}
          <div className="bg-white border border-slate-200/90 rounded-2xl p-4 flex flex-col md:flex-row md:items-center justify-between gap-3 shadow-xs">
            <div className="flex items-center gap-2 flex-wrap">
              {(['all', 'student_innovator', 'media'] as const).map((t) => (
                <button
                  key={t}
                  onClick={() => setAppTypeFilter(t)}
                  className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                    appTypeFilter === t
                      ? 'bg-[#0A3D27] text-white shadow-xs'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  {t === 'all'
                    ? 'All Applications'
                    : t === 'student_innovator'
                    ? 'Student Innovator Pods'
                    : 'Media Accreditation'}
                </button>
              ))}
            </div>

            <div className="flex items-center gap-2">
              {(['all', 'pending', 'approved', 'rejected'] as const).map((st) => (
                <button
                  key={st}
                  onClick={() => setAppStatusFilter(st)}
                  className={`px-2.5 py-1 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                    appStatusFilter === st
                      ? 'bg-slate-900 text-white'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  {st === 'all'
                    ? 'All'
                    : st === 'pending'
                    ? 'Pending'
                    : st === 'approved'
                    ? 'Approved'
                    : 'Rejected'}
                </button>
              ))}
            </div>
          </div>

          {/* Applications Grid / Table */}
          <div className="space-y-3">
            {applicationsQuery.isLoading ? (
              <div className="p-12 text-center text-slate-500 bg-white rounded-2xl border border-slate-200">
                <RotateCw className="w-5 h-5 animate-spin mx-auto text-[#0A3D27] mb-2" />
                <span>Loading applications inbox...</span>
              </div>
            ) : applicationsList.length === 0 ? (
              <div className="p-12 text-center text-slate-400 bg-white rounded-2xl border border-slate-200">
                No applications found in this queue.
              </div>
            ) : (
              applicationsList.map((app) => (
                <div
                  key={app.id}
                  className="bg-white border border-slate-200/90 rounded-2xl p-4 shadow-xs hover:border-slate-300 transition-all flex flex-col md:flex-row md:items-center justify-between gap-4"
                >
                  <div className="space-y-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-bold text-slate-900 text-sm">{app.applicantName}</span>
                      <span
                        className={`px-2 py-0.5 rounded-md text-[10px] font-black uppercase ${
                          app.type === 'student_innovator'
                            ? 'bg-lime-50 text-lime-800 border border-lime-200'
                            : app.type === 'speaker'
                            ? 'bg-rose-50 text-rose-800 border border-rose-200'
                            : 'bg-orange-50 text-orange-800 border border-orange-200'
                        }`}
                      >
                        {app.type.replace('_', ' ')}
                      </span>
                      <span
                        className={`px-2 py-0.5 rounded-md text-[10px] font-bold ${
                          app.status === 'approved'
                            ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                            : app.status === 'rejected'
                            ? 'bg-rose-50 text-rose-700 border border-rose-200'
                            : 'bg-amber-50 text-amber-700 border border-amber-200'
                        }`}
                      >
                        {app.status.toUpperCase()}
                      </span>
                    </div>

                    <div className="text-xs text-slate-600 flex items-center gap-2 flex-wrap">
                      <span>{app.jobTitle || 'Applicant'}</span>
                      <span>•</span>
                      <span className="font-semibold text-[#0A3D27]">{app.organization || 'Independent'}</span>
                      <span>•</span>
                      <span className="font-mono text-slate-400">{app.applicantEmail}</span>
                    </div>

                    {/* Specific details rendering */}
                    {app.details && (
                      <div className="mt-2 text-xs bg-slate-50 p-2.5 rounded-xl border border-slate-200 text-slate-700 space-y-1">
                        {app.details.projectTitle && (
                          <div className="font-bold text-slate-900">
                            Project: {app.details.projectTitle}
                          </div>
                        )}
                        {app.details.problemSolved && (
                          <div className="text-[11px] text-slate-600">
                            Solved: {app.details.problemSolved}
                          </div>
                        )}
                        {app.details.prototypeStage && (
                          <div className="text-[10px] text-slate-500 font-semibold">
                            Stage: {app.details.prototypeStage} • Supervisor: {app.details.supervisor || 'N/A'}
                          </div>
                        )}
                        {app.details.teamMembers && (
                          <div className="text-[10px] text-slate-500">
                            Team: {app.details.teamMembers.join(', ')}
                          </div>
                        )}
                      </div>
                    )}

                    {app.assignedPod && (
                      <div className="text-xs text-lime-900 font-bold mt-1">
                        Allocated Pod: <span className="bg-lime-100 px-1.5 py-0.5 rounded">{app.assignedPod}</span>
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 shrink-0">
                    {app.status === 'pending' ? (
                      <>
                        <button
                          onClick={() => setApprovingApp(app)}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-[#0A3D27] hover:bg-[#062B1B] text-white rounded-xl text-xs font-bold shadow-xs cursor-pointer"
                        >
                          <CheckCircle2 className="w-3.5 h-3.5" />
                          <span>Approve &amp; Provision Badge</span>
                        </button>

                        <button
                          onClick={() => setRejectingApp(app)}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-rose-50 hover:bg-rose-100 text-rose-700 border border-rose-200 rounded-xl text-xs font-bold transition-colors cursor-pointer"
                        >
                          <XCircle className="w-3.5 h-3.5" />
                          <span>Reject</span>
                        </button>
                      </>
                    ) : (
                      <div className="text-xs text-slate-400">
                        Reviewed by {app.reviewedBy || 'Admin'}
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* ======================================================== */}
      {/* TAB 3: SPONSOR PASS ALLOCATIONS                         */}
      {/* ======================================================== */}
      {activeMainTab === 'sponsors' && (
        <div className="space-y-4">
          <div className="bg-white border border-slate-200/90 rounded-2xl p-4 shadow-xs">
            <h4 className="text-xs font-bold text-slate-900 uppercase tracking-wider mb-2">
              Sponsor Tier Pass Quotas
            </h4>
            <p className="text-xs text-slate-500 mb-4">
              Preset VIP, Delegate, and Exhibitor ticket allocations issued to corporate sponsors by tier or contract.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {sponsorLimits.map((limit) => (
                <div key={limit.id} className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="font-black text-slate-900 uppercase tracking-wide text-xs">
                      {limit.sponsorTier} Sponsor
                    </span>
                    {limit.companyName && (
                      <span className="text-[10px] font-bold bg-[#FAF5EA] text-[#785613] px-2 py-0.5 rounded border border-[#E6C687]">
                        {limit.companyName}
                      </span>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-2 text-xs pt-2 border-t border-slate-200">
                    <div className="bg-white p-2 rounded-lg border border-slate-200">
                      <span className="text-[10px] text-slate-500 uppercase font-bold block">VIP Passes</span>
                      <span className="text-base font-black text-[#C5A059]">{limit.vipPassLimit}</span>
                    </div>
                    <div className="bg-white p-2 rounded-lg border border-slate-200">
                      <span className="text-[10px] text-slate-500 uppercase font-bold block">Delegate</span>
                      <span className="text-base font-black text-purple-700">{limit.delegatePassLimit}</span>
                    </div>
                    <div className="bg-white p-2 rounded-lg border border-slate-200">
                      <span className="text-[10px] text-slate-500 uppercase font-bold block">Exhibitor</span>
                      <span className="text-base font-black text-sky-700">{limit.exhibitorPassLimit}</span>
                    </div>
                    <div className="bg-white p-2 rounded-lg border border-slate-200">
                      <span className="text-[10px] text-slate-500 uppercase font-bold block">Visitor</span>
                      <span className="text-base font-black text-emerald-700">{limit.visitorPassLimit}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ======================================================== */}
      {/* MODAL: ADD PARTICIPANT MANUALLY                          */}
      {/* ======================================================== */}
      {isAddParticipantOpen && (
        <div className="fixed inset-0 z-50 bg-slate-950/60 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white border border-slate-200 rounded-3xl w-full max-w-lg overflow-hidden shadow-2xl animate-in fade-in zoom-in-95 duration-200">
            <div className="p-5 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
              <h3 className="font-black text-slate-900 text-sm">Add New Participant (Manual Registration)</h3>
              <button
                onClick={() => setIsAddParticipantOpen(false)}
                className="p-1 hover:bg-slate-200 rounded-lg text-slate-500"
              >
                ✕
              </button>
            </div>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                addParticipantMutation.mutate(newParticipant);
              }}
              className="p-5 space-y-3 text-xs"
            >
              <div>
                <label className="block text-slate-700 font-bold mb-1">Full Name *</label>
                <input
                  type="text"
                  required
                  value={newParticipant.fullName}
                  onChange={(e) => setNewParticipant({ ...newParticipant, fullName: e.target.value })}
                  className="w-full px-3 py-2 border border-slate-200 rounded-xl"
                  placeholder="e.g. Dr. Rajesh Kumar"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-slate-700 font-bold mb-1">Email *</label>
                  <input
                    type="email"
                    required
                    value={newParticipant.email}
                    onChange={(e) => setNewParticipant({ ...newParticipant, email: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-xl"
                    placeholder="official@company.com"
                  />
                </div>
                <div>
                  <label className="block text-slate-700 font-bold mb-1">Phone *</label>
                  <input
                    type="text"
                    required
                    value={newParticipant.phone}
                    onChange={(e) => setNewParticipant({ ...newParticipant, phone: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-xl"
                    placeholder="+91 98765 43210"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-slate-700 font-bold mb-1">Company / Organization</label>
                  <input
                    type="text"
                    value={newParticipant.companyName || ''}
                    onChange={(e) => setNewParticipant({ ...newParticipant, companyName: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-xl"
                    placeholder="ICAR / AgroCorp Ltd"
                  />
                </div>
                <div>
                  <label className="block text-slate-700 font-bold mb-1">Job Title</label>
                  <input
                    type="text"
                    value={newParticipant.jobTitle || ''}
                    onChange={(e) => setNewParticipant({ ...newParticipant, jobTitle: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-xl"
                    placeholder="Chief Agronomist"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-slate-700 font-bold mb-1">Primary Role *</label>
                  <select
                    value={newParticipant.primaryRole}
                    onChange={(e) =>
                      setNewParticipant({
                        ...newParticipant,
                        primaryRole: e.target.value,
                        tier:
                          e.target.value === 'vip'
                            ? 'vip'
                            : e.target.value === 'delegate'
                            ? 'conference_delegate'
                            : 'trade_visitor',
                      })
                    }
                    className="w-full px-3 py-2 border border-slate-200 rounded-xl bg-white"
                  >
                    <option value="vip">VIP Guest</option>
                    <option value="speaker">Speaker</option>
                    <option value="student_innovator">Student Innovator</option>
                    <option value="media">Media</option>
                    <option value="organiser">Organiser</option>
                    <option value="sponsor">Sponsor</option>
                    <option value="exhibitor">Exhibitor</option>
                    <option value="delegate">Conference Delegate</option>
                    <option value="visitor">Trade Visitor</option>
                    <option value="contractor">Contractor</option>
                  </select>
                </div>

                <div>
                  <label className="block text-slate-700 font-bold mb-1">Stall or Pod Number</label>
                  <input
                    type="text"
                    value={newParticipant.stallNumber || newParticipant.assignedPod || ''}
                    onChange={(e) =>
                      setNewParticipant({
                        ...newParticipant,
                        stallNumber: e.target.value,
                        assignedPod: e.target.value.startsWith('POD') ? e.target.value : undefined,
                      })
                    }
                    className="w-full px-3 py-2 border border-slate-200 rounded-xl"
                    placeholder="e.g. A-101 or POD-03"
                  />
                </div>
              </div>

              {/* Privacy toggles */}
              <div className="pt-2 border-t border-slate-100 space-y-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={newParticipant.directoryOptOut}
                    onChange={(e) =>
                      setNewParticipant({ ...newParticipant, directoryOptOut: e.target.checked })
                    }
                    className="rounded text-[#0A3D27] focus:ring-[#0A3D27]"
                  />
                  <span className="text-slate-700">Hide profile from public participant directory</span>
                </label>

                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={newParticipant.shareContactOptOut}
                    onChange={(e) =>
                      setNewParticipant({ ...newParticipant, shareContactOptOut: e.target.checked })
                    }
                    className="rounded text-[#0A3D27] focus:ring-[#0A3D27]"
                  />
                  <span className="text-slate-700">Opt-out of contact sharing during badge scans</span>
                </label>
              </div>

              {addParticipantMutation.isError && (
                <div className="p-2.5 rounded-xl bg-rose-50 text-rose-700 text-[11px] border border-rose-200">
                  {(addParticipantMutation.error as any)?.message || 'Failed to create participant'}
                </div>
              )}

              <div className="flex justify-end gap-2 pt-3">
                <button
                  type="button"
                  onClick={() => setIsAddParticipantOpen(false)}
                  className="px-4 py-2 border border-slate-200 rounded-xl text-slate-600 hover:bg-slate-50 cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={addParticipantMutation.isPending}
                  className="px-4 py-2 bg-[#0A3D27] hover:bg-[#062B1B] text-white font-bold rounded-xl cursor-pointer"
                >
                  {addParticipantMutation.isPending ? 'Provisioning...' : 'Provision Badge'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ======================================================== */}
      {/* MODAL: APPROVE APPLICATION (ASSIGN POD & PROVISION)      */}
      {/* ======================================================== */}
      {approvingApp && (
        <div className="fixed inset-0 z-50 bg-slate-950/60 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white border border-slate-200 rounded-3xl w-full max-w-md overflow-hidden shadow-2xl p-5 space-y-4">
            <h3 className="font-black text-slate-900 text-sm">Approve Application</h3>
            <p className="text-xs text-slate-500">
              Approving <strong className="text-slate-900">{approvingApp.applicantName}</strong> will immediately create an attendee profile and issue an active badge credential.
            </p>

            {approvingApp.type === 'student_innovator' && (
              <div>
                <label className="block text-slate-700 font-bold mb-1 text-xs">Assign Innovation Pod</label>
                <input
                  type="text"
                  value={approvePod}
                  onChange={(e) => setApprovePod(e.target.value)}
                  placeholder="e.g. POD-01, POD-02"
                  className="w-full px-3 py-2 border border-slate-200 rounded-xl text-xs"
                />
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <button
                onClick={() => setApprovingApp(null)}
                className="px-3 py-1.5 border border-slate-200 rounded-xl text-xs text-slate-600"
              >
                Cancel
              </button>
              <button
                onClick={() =>
                  approveAppMutation.mutate({
                    id: approvingApp.id,
                    payload: { assignedPod: approvePod || undefined },
                  })
                }
                disabled={approveAppMutation.isPending}
                className="px-4 py-1.5 bg-[#0A3D27] hover:bg-[#062B1B] text-white font-bold rounded-xl text-xs"
              >
                {approveAppMutation.isPending ? 'Approving...' : 'Confirm & Issue Badge'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ======================================================== */}
      {/* MODAL: REJECT APPLICATION                                */}
      {/* ======================================================== */}
      {rejectingApp && (
        <div className="fixed inset-0 z-50 bg-slate-950/60 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white border border-slate-200 rounded-3xl w-full max-w-md overflow-hidden shadow-2xl p-5 space-y-4">
            <h3 className="font-black text-slate-900 text-sm">Reject Application</h3>
            <p className="text-xs text-slate-500">
              Provide reason for rejecting application of <strong className="text-slate-900">{rejectingApp.applicantName}</strong>.
            </p>

            <div>
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Reason for committee rejection..."
                rows={3}
                className="w-full p-3 border border-slate-200 rounded-xl text-xs"
              />
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <button
                onClick={() => setRejectingApp(null)}
                className="px-3 py-1.5 border border-slate-200 rounded-xl text-xs text-slate-600"
              >
                Cancel
              </button>
              <button
                onClick={() =>
                  rejectAppMutation.mutate({
                    id: rejectingApp.id,
                    reason: rejectReason || 'Did not meet evaluation criteria.',
                  })
                }
                disabled={rejectAppMutation.isPending}
                className="px-4 py-1.5 bg-rose-600 hover:bg-rose-700 text-white font-bold rounded-xl text-xs"
              >
                {rejectAppMutation.isPending ? 'Rejecting...' : 'Confirm Rejection'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminParticipantsApplications;
